import React, { useMemo, useState } from 'react';
import _debounce from 'lodash/debounce';
import useControlledState from '../../hooks/useControlledState';
import PopoverButton from './PopoverButton';
import PopoverContent from './PopoverContent';
import PopoverContext from './PopoverContext';
import type { PopoverProps } from './types';

const MOUSE_EVENT_DELAY_IN_MS = 200;

function Popover(props: PopoverProps) {
  const {
    children,
    onOpenChange: onChange,
    open: controlledValue,
    initialValue = false,
    anchorEl: initialAnchorEl,
    onClose,
    triggerType = 'click',
  } = props;
  const [isVisible, toggleVisibility] = useControlledState({
    controlledValue,
    defaultValue: initialValue,
    onChange,
  });

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(initialAnchorEl ?? null);
  const value = useMemo(() => {
    const onMouseMove =
      triggerType !== 'hover'
        ? undefined
        : _debounce((event: React.MouseEvent) => {
            if (event.type === 'mouseenter') {
              return toggleVisibility(true);
            }
            if (event.type === 'mouseleave') {
              return toggleVisibility(false);
            }
          }, MOUSE_EVENT_DELAY_IN_MS);

    return {
      anchorEl,
      onClose: () => {
        toggleVisibility(false);
        onClose?.();
      },
      onOpen: () => toggleVisibility(true),
      onOpenChange: toggleVisibility,
      open: Boolean(isVisible),
      setAnchorEl,
      triggerType,
      onMouseMove,
    };
  }, [anchorEl, toggleVisibility, isVisible, triggerType, onClose]);

  return (
    <PopoverContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </PopoverContext.Provider>
  );
}

Popover.displayName = 'Popover';

const PopoverNamespace = Object.assign(Popover, {
  Button: PopoverButton,
  Content: PopoverContent,
});

export default PopoverNamespace;
